import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { assets } from '../config/assets'
import { carouselItems } from '../data/mock/carousel'

const SWIPE_THRESHOLD = 50

export default function SubscriptionCarousel() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const total = carouselItems.length
  // Triple the items so we can slide infinitely through the middle copy.
  const extendedItems = [...carouselItems, ...carouselItems, ...carouselItems]
  const baseOffset = total

  const [currentIndex, setCurrentIndex] = useState(baseOffset + 1)
  const [isAnimating, setIsAnimating] = useState(false)
  const [hoveredId, setHoveredId] = useState(null)
  const [isMobile, setIsMobile] = useState(false)
  const touchStartX = useRef(null)

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)')
    setIsMobile(mql.matches)
    const handler = (e) => setIsMobile(e.matches)
    mql.addEventListener('change', handler)
    return () => mql.removeEventListener('change', handler)
  }, [])

  const activeItem = extendedItems[currentIndex]
  const focusedId = hoveredId ?? activeItem.id

  const goTo = useCallback((index) => {
    setIsAnimating(true)
    setHoveredId(null)
    setCurrentIndex(index)
  }, [])

  const goPrev = useCallback(() => {
    goTo(currentIndex - 1)
  }, [currentIndex, goTo])

  const goNext = useCallback(() => {
    goTo(currentIndex + 1)
  }, [currentIndex, goTo])

  const handleTransitionEnd = useCallback(() => {
    setIsAnimating(false)
    if (currentIndex < baseOffset) {
      setCurrentIndex(currentIndex + total)
    } else if (currentIndex >= baseOffset + total) {
      setCurrentIndex(currentIndex - total)
    }
  }, [currentIndex, baseOffset, total])

  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX
  }, [])

  const handleTouchEnd = useCallback(
    (e) => {
      if (touchStartX.current === null) return
      const diff = touchStartX.current - e.changedTouches[0].clientX
      if (Math.abs(diff) > SWIPE_THRESHOLD) {
        diff > 0 ? goNext() : goPrev()
      }
      touchStartX.current = null
    },
    [goNext, goPrev]
  )

  // Mobile: one item per view => translate by currentIndex * 100%
  // Desktop: slide one item at a time; each step = small poster width + gap
  const stepOffset = currentIndex - (baseOffset + 1)
  const mobileTransform = `translateX(-${currentIndex * 100}%)`
  const desktopTransform = `translateX(calc(${-stepOffset} * var(--item-step)))`

  return (
    <div
      className="relative w-full md:w-fit mx-auto px-10 md:px-16 h-[40vh] md:h-[70vh] md:translate-y-[5vh]"
      style={{
        '--poster-aspect': '0.702',
        '--poster-height-lg': 'min(70vh, calc((95vw - 2rem - 2 * var(--arrow-padding)) / 1.502))',
        '--poster-height-sm': 'calc(var(--poster-height-lg) * 0.57)',
        '--poster-width-lg': 'calc(var(--poster-height-lg) * var(--poster-aspect))',
        '--poster-width-sm': 'calc(var(--poster-height-sm) * var(--poster-aspect))',
        '--poster-gap': '1rem',
        '--arrow-padding': '2.5rem',
        '--step-width': 'calc(2 * var(--poster-width-sm) + var(--poster-width-lg) + 2 * var(--poster-gap))',
        '--item-step': 'calc(var(--poster-width-sm) + var(--poster-gap))',
      }}
    >
      <div
        className="h-full mx-auto overflow-hidden touch-pan-y md:w-[var(--step-width)]"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className={`flex h-full items-center ${isAnimating ? 'transition-transform duration-500 ease-out' : ''}`}
          style={{ transform: isMobile ? mobileTransform : desktopTransform }}
          onTransitionEnd={handleTransitionEnd}
        >
          {extendedItems.map((item, idx) => {
            const isFocused = item.id === focusedId
            const isLastInGroup = (idx + 1) % 3 === 0
            return (
              <div
                key={`${item.id}-${idx}`}
                className={`flex-shrink-0 h-full flex items-center justify-center transition-[width] duration-500 ease-out ${
                  isMobile ? 'w-full' : isFocused ? 'w-[var(--poster-width-lg)]' : 'w-[var(--poster-width-sm)]'
                }`}
                style={!isMobile ? { marginRight: isLastInGroup ? 0 : 'var(--poster-gap)' } : undefined}
              >
                <button
                  type="button"
                  onClick={() => navigate(item.to)}
                  onMouseEnter={() => setHoveredId(item.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  aria-label={t(item.titleKey)}
                  className={`transition-[height] duration-500 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 overflow-hidden ${
                    isFocused ? 'z-10' : 'opacity-90'
                  }`}
                style={{
                  height: isMobile
                    ? '40vh'
                    : isFocused
                    ? 'var(--poster-height-lg)'
                    : 'var(--poster-height-sm)',
                  aspectRatio: 'var(--poster-aspect)',
                  maxWidth: isMobile ? '85%' : '100%',
                }}
                >
                  <img
                    src={item.image}
                    alt=""
                    draggable={false}
                    className={`h-full w-full drop-shadow-2xl ${isMobile ? 'object-contain' : 'object-cover'}`}
                  />
                </button>
              </div>
            )
          })}
        </div>
      </div>

      <button
        type="button"
        onClick={goPrev}
        aria-label={t('carousel.previous')}
        className="absolute left-0 top-1/2 -translate-y-1/2 z-30 appearance-none bg-transparent border-none p-0 cursor-pointer transition-transform active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
      >
        <img
          src={assets.arrowLeft}
          alt=""
          draggable={false}
          className="h-10 md:h-24 w-auto drop-shadow-2xl"
        />
      </button>

      <button
        type="button"
        onClick={goNext}
        aria-label={t('carousel.next')}
        className="absolute right-0 top-1/2 -translate-y-1/2 z-30 appearance-none bg-transparent border-none p-0 cursor-pointer transition-transform active:scale-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold"
      >
        <img
          src={assets.arrowRight}
          alt=""
          draggable={false}
          className="h-10 md:h-24 w-auto drop-shadow-2xl"
        />
      </button>
    </div>
  )
}
